import {FormattedMessage, useIntl} from "react-intl";
import {Button, Col, Divider, Form, Input, Modal, Row, Select, Spin, Upload} from "antd";
import React, {ReactNode, SyntheticEvent, useContext, useEffect, useState, useRef} from "react";
import {
    Organization
} from "../../api-client";
import {getInvoicesApi, getOrganizationApi} from "../../api/apiInstances";
import '../../assets/css/Dentobook.css';
import {
    CameraOutlined,
    CaretDownOutlined, CloseOutlined,
    EnvironmentOutlined,
    FileTextOutlined, InfoCircleOutlined,
    MedicineBoxOutlined
} from "@ant-design/icons";
import UserIcon from "../common/UserIcon";
import FormButtons from "../common/FormButtons";
import {AlertContext} from "../../context/AlertContext";
import {RcFile, UploadProps} from "antd/es/upload";
import {useNavigate} from "react-router-dom";
import {messages} from "../../i18n/health-area";
import {healthRegionMessages} from "../../i18n/health-regions";
import {isMobileOnly} from "react-device-detect";
import {ExitPromptContext} from "../../context/ExitPromptContext";
import {checkFormDirty} from "../../i18n/utils";
import {allCountries, getCurrencies, getCurrencyByCountry} from "../../i18n/countries";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import logger from "../../utils/logger";
import InvoiceConfigSetupModal from "./InvoiceConfigSetupModal";
import {ALERT_DURATIONS} from "../../constants/alerts";

interface GeneralFormProps {
    organization: Organization;
    setEditMode?: Function;
    children?: React.JSX.Element | React.JSX.Element[];
    onClose: () => void;
}

interface Region {
    key: string;
    value: string;
}

interface FieldsTranslations {
    [key: string]: string;
}

interface GeneralFormProps {
    organization: Organization;
    onClose: () => void;
}

interface Currency {
    key: string;
    value: string;
    labelBg: string;
    labelEn: string;
}

const GeneralForm: React.FC<Readonly<GeneralFormProps>> = ({organization, onClose}) => {
    const intl = useIntl();
    const PHOTO_URL_PREFIX = '/api/organizations/';
    const PHOTO_URL_SUFFIX = '/avatar/';

    const {locale, setUserOrganization} = useContext(DentobookApplicationContext);
    const {setFormDirty} = useContext(ExitPromptContext);
    const {setVisible, setError, setSuccessMessage} = useContext(AlertContext);

    const duplicateEmailError: boolean = false;

    const [isAbroad, setAbroad] = useState<boolean>(organization.country === 'BG');
    const [imagePreview, setImagePreview] = useState<string>('');
    const [imagePreviewJsx, setImagePreviewJsx] = useState<ReactNode>();
    const [formOrganizationDetails, setFormOrganizationDetails] = useState(organization);
    const [avatar, setAvatar] = useState<RcFile | undefined>(undefined);
    const [avatarMouseIn, setAvatarMouseIn] = useState<boolean>(false);
    const [avatarUploading, setAvatarUploading] = useState<boolean>(false);
    const [filteredRegions, setFilteredRegions] = useState<Region[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isInvoiceNumberEditable, setIsInvoiceNumberEditable] = useState(true);
    const [isProformaInvoiceNumberEditable, setIsProformaInvoiceNumberEditable] = useState(true);
    const [modalType, setModalType] = useState<'invoice' | 'proforma'>('invoice');
    const [showInvoiceConfigSetup, setShowInvoiceConfigSetup] = useState(false);
    const [isCheckingConfig, setIsCheckingConfig] = useState(true);
    const hasCheckedInvoiceConfig = useRef(false);

    const handleMouseEnter = () => setAvatarMouseIn(true);
    const handleMouseLeave = () => setAvatarMouseIn(false);
    const handleKeyDown = (event: { key: string; }) => {
        if (event.key === 'Enter' || event.key === ' ') {
            setAvatarMouseIn(prev => !prev);
        }
    };

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const translations: FieldsTranslations = messages[intl.locale].towns;
    const organizationApi = getOrganizationApi();
    const invoiceConfigApi = getInvoicesApi();
    const invoicesApi = getInvoicesApi();

    const navigate = useNavigate();

    const [form] = Form.useForm();

    const handleHealthAreaChange = (value: string) => {
        // Update filtered regions immediately and reset healthRegion
        updateFilteredRegions(value);

        setFormOrganizationDetails(prevState => ({
            ...prevState,
            healthArea: value,
            healthRegion: '' // Reset healthRegion when healthArea changes
        }));
        form.setFieldsValue({healthRegion: ''});
    };


    const handleHealthRegionChange = (value: string) => {
        setFormOrganizationDetails({...formOrganizationDetails, healthRegion: value});
    };

    const updateFilteredRegions = (selectedArea: string) => {
        const regions = Object.entries(healthRegionMessages[intl.locale].healthRegions)
            .filter(([key]) => key.startsWith(selectedArea))
            .map(([key, value]) => ({key, value: value as string}));

        setFilteredRegions(regions);
    };

    const showModal = (modalType: 'invoice' | 'proforma') => {
        setModalType(modalType);
        setIsModalVisible(true);
    };

    const handleModalOk = () => {
        setIsModalVisible(false);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
    };

    const postInvoiceConfig = async (invoiceNumber: string | undefined, organizationId: any, documentType: string) => {
        if (invoiceNumber && organizationId) {
            const invoiceConfig = {
                prefix: '',
                lastNumber: parseInt(invoiceNumber, 10),
                digitsCount: invoiceNumber.length,
                documentType: documentType,
                organizationId: organizationId,
                formattedNumber: invoiceNumber,
                invoiceNumber: invoiceNumber
            };
            await invoiceConfigApi.createOrUpdateInvoiceConfig(invoiceConfig);
        }
    };


    const [formInitialState, setFormInitialState] = useState<Object | undefined>(undefined);

    useEffect(() => {
        if (!formInitialState) {
            setFormInitialState(form.getFieldsValue(true));
        }
    }, [form]);
    useEffect(() => {
        const checkInvoiceConfig = async () => {
            // Only check once per component mount
            if (organization.id != null && !hasCheckedInvoiceConfig.current) {
                hasCheckedInvoiceConfig.current = true;

                let invoiceConfigMissing = false;
                let proformaConfigMissing = false;

                // Check invoice configuration
                try {
                    const invoiceResponse = await invoicesApi.fetchInvoiceConfiguration(organization.id, "INVOICE");
                    setIsInvoiceNumberEditable(!invoiceResponse?.data);
                } catch (error: any) {
                    if (error?.response?.status === 404) {
                        invoiceConfigMissing = true;
                        setIsInvoiceNumberEditable(true);
                    } else {
                        logger.error('Error fetching invoice configuration:', error);
                    }
                }

                // Check proforma invoice configuration
                try {
                    const proformaResponse = await invoicesApi.fetchInvoiceConfiguration(organization.id, "PROFORMA_INVOICE");
                    setIsProformaInvoiceNumberEditable(!proformaResponse?.data);
                } catch (error: any) {
                    if (error?.response?.status === 404) {
                        proformaConfigMissing = true;
                        setIsProformaInvoiceNumberEditable(true);
                    } else {
                        logger.error('Error fetching proforma invoice configuration:', error);
                    }
                }

                // Show setup modal if either config is missing
                if (invoiceConfigMissing || proformaConfigMissing) {
                    setShowInvoiceConfigSetup(true);
                }

                // Mark checking as complete
                setIsCheckingConfig(false);
            }
        };

        checkInvoiceConfig();
    }, [organization.id]);

    useEffect(() => {
        if (formOrganizationDetails.healthArea) {
            updateFilteredRegions(formOrganizationDetails.healthArea);
        }
    }, [intl.locale, formOrganizationDetails.healthArea]);

    useEffect(() => {
        if (imagePreview) {
            setImagePreviewJsx(
                <input
                    type="button"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onKeyDown={handleKeyDown}
                    className="avatar-upload-photo-img"
                    style={{
                        backgroundImage: `url(${imagePreview})`,
                        width: '150px',
                        height: '150px',
                        backgroundSize: 'cover',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                    alt="avatar"
                />
            );
        } else if (!organization.avatar) {
            setImagePreviewJsx(
                <button
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onKeyDown={handleKeyDown}
                    className="avatar-upload-photo-img"
                    style={{border: 'none', cursor: 'pointer', background: 'none', marginLeft: -6}}
                >
                    <UserIcon size="large" className="mb-4"/>
                </button>
            );
        } else {
            setImagePreviewJsx(
                <input
                    type="button"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onKeyDown={handleKeyDown}
                    className="avatar-upload-photo-img"
                    style={{
                        backgroundImage: `url(${organization.avatarUrl || PHOTO_URL_PREFIX + organization.id + PHOTO_URL_SUFFIX})`,
                        width: '150px',
                        height: '150px',
                        backgroundSize: 'cover',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                    alt="avatar"
                />
            );
        }
    }, [imagePreview, organization.avatar, organization.avatarUrl]);

    const handleSubmit = async (e: SyntheticEvent) => {
        e.preventDefault();

        try {
            await form.validateFields();
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");

            const checkAndPostInvoiceConfig = async (organizationId: string, invoiceNumber: string, documentType: "INVOICE" | "PROFORMA_INVOICE") => {
                try {
                    const invoiceConfig = await invoicesApi.fetchInvoiceConfiguration(organizationId, documentType);
                    if (!invoiceConfig?.data) {
                        await postInvoiceConfig(invoiceNumber, organizationId, documentType);
                    }
                } catch (error) {
                    if ((error as { response?: { status?: number } })?.response?.status === 404) {
                        // Config doesn't exist yet — create it
                        await postInvoiceConfig(invoiceNumber, organizationId, documentType);
                    } else {
                        logger.error(`Error fetching ${documentType} configuration:`, error);
                    }
                }
            };

            const updateOrganizationAvatar = async (organizationId: string, avatarFile: File | null) => {
                if (avatarFile) {
                    try {
                        setAvatarUploading(true);
                        await organizationApi.uploadOrganizationAvatar(organizationId, avatarFile);
                        setAvatarUploading(false);
                        setError('');
                        setSuccessMessage(intl.formatMessage({ id: 'organization_updated' }));
                        setVisible(true);
                        setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                        navigate('/organization', {replace: true});
                    } catch (error) {
                        setAvatarUploading(false);
                        logger.log('Error updating avatar', error);
                    }
                } else {
                    setError('');
                    setSuccessMessage(intl.formatMessage({ id: 'organization_updated' }));
                    setVisible(true);
                    setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                    navigate('/organization', {replace: true});
                }
            };

            const handleOrganizationResult = async (organizationId: string) => {
                if (formOrganizationDetails.invoiceNumber) {
                    await checkAndPostInvoiceConfig(organizationId, formOrganizationDetails.invoiceNumber, "INVOICE");
                }
                if (formOrganizationDetails.proformaInvoiceNumber) {
                    await checkAndPostInvoiceConfig(organizationId, formOrganizationDetails.proformaInvoiceNumber, "PROFORMA_INVOICE");
                }

                await updateOrganizationAvatar(organizationId, avatar || null);
                onClose();
            };

            if (!organization.id) {
                try {
                    const result = await organizationApi.createOrganization(formOrganizationDetails);
                    const organizationId = result.data.id;
                    if (organizationId != null) {
                        await handleOrganizationResult(organizationId);
                    }
                } catch (error) {
                    logger.log('Error creating organization', error);
                }
            } else {
                try {
                    const result = await organizationApi.updateOrganization(organization.id, formOrganizationDetails);
                    setUserOrganization(result.data);
                    const organizationId = result.data.id;
                    if (organizationId != null) {
                        await handleOrganizationResult(organizationId);
                    }
                } catch (error) {
                    logger.log('Error updating organization', error);
                }
            }
        } catch (err: any) {
            const blankFieldExists = Object.values(err.values).filter(item => !item).length > 0;
            if (blankFieldExists) {
                setError(intl.formatMessage({id: 'all_fields_required'}));
                setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
            }
        }
    };


    const props: UploadProps = {
        name: 'file',
        customRequest: () => {
        },
        showUploadList: false,
        onChange(info) {
            setAvatar(info.file.originFileObj);

            setImagePreview(URL.createObjectURL(info.file.originFileObj as Blob));
        },
    };

    const handleOk = () => {
        setFormDirty(false);
        setIsModalOpen(false);

        onClose();
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const footer = <div className="modal-btn-wrapper">
        <Button className="modal-btn" onClick={handleCancel}>
            {<FormattedMessage id="cancel"/>}
        </Button>
        <Button className="modal-btn" type="primary" onClick={handleOk}
        >
            {<FormattedMessage id="confirm"/>}
        </Button>
    </div>;

    const currencyDropdownOptions = () => getCurrencies().map((countryItem: Currency) => {
        return {
            key: countryItem.key,
            value: countryItem.value,
            label: locale === 'BG' ? countryItem.labelBg : countryItem.labelEn
        };
    });

    const handleCountryChange = (e: string) => {
        setAbroad(e === 'BG');
        formOrganizationDetails.country = e;
    }

    if (isCheckingConfig) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" tip={intl.formatMessage({ id: 'loading' })} />
            </div>
        );
    }

    return (
        <>
            <Modal
                transitionName=""
                footer={footer}
                className="dentobook-modal"
                title={<FormattedMessage id={!organization.id ? "org_create" : "org_edit"}/>}
                closeIcon={<CloseOutlined className={'icon-24px'} onClick={handleCancel}/>}
                open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
                <h3 className={"dentobook-content font-weight-500 font-24 text-align-center " + (isMobileOnly ? "mt-112" : "")}>
                    <FormattedMessage id="unsaved_changes"/></h3>
            </Modal>
            <Form form={form} name="horizontal_login" layout="vertical"
                  onFieldsChange={(changedFields, allFields) => setFormDirty(checkFormDirty(allFields, formInitialState))}>
                <h4><span className="text-blue mr-4"><MedicineBoxOutlined/></span> {<FormattedMessage
                    id="main_information"/>}
                </h4>
                <Form.Item
                    label={<FormattedMessage id="logo"/>}
                    className={"dentobook-form-item dentobook-simple-input"}
                >
                    <div style={{ position: 'relative' }}>
                        <Upload {...props} disabled={avatarUploading}>
                            <div className={avatarMouseIn && !avatarUploading ? "avatar-upload-photo-mask" : ""}>
                                <CameraOutlined className={avatarMouseIn && !avatarUploading ? "avatar-upload-icon" : "d-none"}/>
                                {imagePreviewJsx}
                            </div>
                        </Upload>
                        {avatarUploading && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '150px',
                                height: '150px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '8px',
                                zIndex: 1000
                            }}>
                                <Spin size="large" />
                            </div>
                        )}
                    </div>
                </Form.Item>
                <Row gutter={16} className="mt-2">
                    <Col flex={0.31}>
                        <Form.Item
                            label={<FormattedMessage id="name"/>}
                            className={"dentobook-form-item dentobook-simple-input input-full-width "}
                            name="FirstName"
                            rules={[
                                {max: 250, message: <FormattedMessage id="max_length_250"/>},
                                {
                                    pattern: /^[a-zA-Zа-яА-Я\-\s\d]+$/,
                                    message: <FormattedMessage id="invalid_field"/>
                                },
                                {required: true, message: <FormattedMessage id="required_field"/>}
                            ]}
                            initialValue={formOrganizationDetails.name}
                        >
                            <Input
                                bordered={false}
                                className="input-ninety-percents dentobook-inputs"
                                value={formOrganizationDetails.name}
                                onChange={e => formOrganizationDetails.name = e.target.value}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    name="currency"
                    label={<FormattedMessage id="currency_label"/>}
                    className="dentobook-form-item">
                    <Select
                        size="large"
                        className="dentobook-input mb-12"
                        showSearch
                        optionFilterProp="label"
                        defaultValue={formOrganizationDetails.currency ?? getCurrencyByCountry(organization.country)}
                        onChange={e => setFormOrganizationDetails(prevState => ({
                            ...prevState,
                            currency: e,
                        }))}
                        suffixIcon={<CaretDownOutlined/>}
                        options={currencyDropdownOptions()}
                    />
                </Form.Item>
                <Divider/>
                <h4><span className="text-blue mr-4"><EnvironmentOutlined/></span> <FormattedMessage id="contact"/>
                </h4>
                <Row gutter={[16, 16]}>
                    <Col md={5}>
                        {/* Email Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input input-full-width"}
                            label={<FormattedMessage id="email"/>}
                            name="email"
                            rules={[{
                                type: "email",
                                message: <FormattedMessage id="invalid_email"/>
                            }]}
                            initialValue={formOrganizationDetails.email}
                        >
                            <Input
                                className="input-ninety-percent"
                                value={formOrganizationDetails.email ?? undefined}
                                onChange={e => formOrganizationDetails.email = e.target.value}
                            />
                        </Form.Item>
                        {duplicateEmailError && (
                            <span className="ant-form-item-required text-red">
                <FormattedMessage id="duplicate_email"/>
            </span>
                        )}
                    </Col>

                    <Col md={5}>
                        {/* Phone Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input input-full-width"}
                            label={<FormattedMessage id="phone_label_two"/>}
                            name="phone"
                            rules={[
                                {
                                    pattern: new RegExp(/^[+()\d\s]+$/gm),
                                    message: <FormattedMessage id="invalid_phone_number"/>
                                }
                            ]}
                            initialValue={formOrganizationDetails.phone}
                        >
                            <Input
                                type="tel"
                                className="dentobook-input input-ninety-percent"
                                value={formOrganizationDetails.phone ?? undefined}
                                onChange={e => formOrganizationDetails.phone = e.target.value}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={5}>
                        {/* Website Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input input-full-width"}
                            label={<FormattedMessage id="website"/>}
                            name="website"
                            initialValue={formOrganizationDetails.website}
                        >
                            <Input
                                className="dentobook-input input-ninety-percent"
                                value={formOrganizationDetails.website ?? undefined}
                                onChange={e => formOrganizationDetails.website = e.target.value}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col lg={5}>
                        {/* Town Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input input-full-width"}
                            label={<FormattedMessage id="town"/>}
                            name="town"
                            rules={[{
                                pattern: /^([a-zA-Z\u0410-\u044F\s]+)$/
                                , message: <FormattedMessage id="letters_only_field"/>
                            },
                                {type: "string", message: <FormattedMessage id="invalid_town"/>}]}
                            initialValue={formOrganizationDetails.town}
                        >
                            <Input
                                className="dentobook-input input-ninety-percent"
                                value={formOrganizationDetails.town ?? undefined}
                                onChange={e => formOrganizationDetails.town = e.target.value}
                            />
                        </Form.Item>
                    </Col>

                    <Col lg={5}>
                        {/* Country Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input input-full-width single-select"}
                            label={<FormattedMessage id="country"/>}
                            name="country"
                            rules={[{
                                pattern: /^([a-zA-Z\u0410-\u044F\s]+)$/
                                , message: <FormattedMessage id="letters_only_field"/>
                            },
                                {
                                    type: "string",
                                    message: <FormattedMessage id="invalid_country"/>
                                }]}
                            initialValue={formOrganizationDetails.country}
                        >
                            <Select
                                showSearch
                                optionFilterProp="children"
                                suffixIcon={<CaretDownOutlined/>}
                                className="dentobook-input input-ninety-percent"
                                value={formOrganizationDetails.country}
                                onChange={handleCountryChange}
                            >
                                {allCountries.map(countryItem => (
                                    <Select.Option key={countryItem.code}
                                                   value={countryItem.code}>
                                        {locale === 'BG' ? countryItem.name_bg : countryItem.name_en}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row>
                    <Col flex={4.85}>
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input input-full-width"}
                            label={<FormattedMessage id="addressOne"/>}
                            name="addressOne"
                            initialValue={formOrganizationDetails.address}
                            rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                        >
                            <Input value={formOrganizationDetails.address ?? undefined}
                                   className="dentobook-input input-ninety-percents"
                                   onChange={e => formOrganizationDetails.address = e.target.value}/>
                        </Form.Item>
                    </Col>
                    <Col flex={0.15}></Col>
                </Row>

                <Divider/>
                <h4><span className="text-blue mr-4"><FileTextOutlined/></span> <FormattedMessage
                    id="company_data"/>
                </h4>
                <Row gutter={24}>
                    <Col md={12}>
                        {/* Website Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input input-full-width "}
                            label={<FormattedMessage id="name"/>}
                            name="companyName"
                            initialValue={formOrganizationDetails.companyName}
                        >
                            <Input
                                className="dentobook-input"
                                value={formOrganizationDetails.companyName ?? undefined}
                                onChange={e => formOrganizationDetails.companyName = e.target.value}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={6}>
                        {/* Phone Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input input-full-width"}
                            label={<FormattedMessage id="uic"/>}
                            name="uic"
                            rules={[
                                {
                                    pattern: new RegExp(/^[\d\s]+$/gm),
                                    message: <FormattedMessage id="invalid_uic_number"/>
                                }
                            ]}
                            initialValue={formOrganizationDetails.uic}
                        >
                            <Input
                                type="tel"
                                className="dentobook-input"
                                value={formOrganizationDetails.uic ?? undefined}
                                onChange={e => formOrganizationDetails.uic = e.target.value}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={6}>
                        {/* Website Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input input-full-width"}
                            label={<FormattedMessage id="rzi_practice_code"/>}
                            name="rzi_practice_code"
                            rules={[
                                {
                                    pattern: /^[a-zA-Z0-9\s]+$/,
                                    message: <FormattedMessage id="invalid_rzi_practice_code"/>
                                }
                            ]}
                            initialValue={formOrganizationDetails.rziPracticeCode}
                        >
                            <Input
                                className="dentobook-input"
                                value={formOrganizationDetails.rziPracticeCode ?? undefined}
                                onChange={e => formOrganizationDetails.rziPracticeCode = e.target.value}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                {isAbroad ?
                    <Row gutter={24}>
                        <Col md={12}>
                            {/* Health Area Input */}
                            <Form.Item
                                className={"dentobook-form-item dentobook-simple-input input-full-width"}
                                label={<FormattedMessage id="healthArea"/>}
                                name="healthArea"
                                initialValue={formOrganizationDetails.healthArea}
                                rules={[
                                    {
                                        pattern: /^[0-9a-zA-Z]*$/,
                                        message: <FormattedMessage id="invalid_field"/>
                                    }, {required: true, message: <FormattedMessage id="required_field"/>}
                                ]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    suffixIcon={<CaretDownOutlined/>}
                                    className="dentobook-input"
                                    value={formOrganizationDetails.healthArea}
                                    onChange={handleHealthAreaChange}
                                >
                                    {Object.entries(translations)
                                        .sort((a, b) => a[1].localeCompare(b[1]))
                                        .map(([key, value]) => (
                                            <Select.Option
                                                key={key}
                                                value={key}
                                                label={`${intl.formatMessage({id: 'rhif'})} ${value}`}
                                            >
                                                <FormattedMessage id="rhif"/> {value}
                                            </Select.Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={12}>
                            {/* Health Region Input */}
                            <Form.Item
                                className={"dentobook-form-item dentobook-simple-input input-full-width mb-32"}
                                label={<FormattedMessage id="healthRegion"/>}
                                name="healthRegion"
                                initialValue={formOrganizationDetails.healthRegion}
                                rules={[
                                    {
                                        message: <FormattedMessage id="letters_only_field"/>
                                    }, {required: true, message: <FormattedMessage id="required_field"/>}
                                ]}
                            >
                                <Select
                                    key={formOrganizationDetails.healthArea} // Add a key to force re-render
                                    showSearch
                                    optionFilterProp="children"
                                    suffixIcon={<CaretDownOutlined/>}
                                    className="dentobook-input"
                                    value={formOrganizationDetails.healthRegion}
                                    onChange={handleHealthRegionChange}
                                    disabled={!formOrganizationDetails.healthArea}
                                >
                                    {filteredRegions.map(({key, value}) => (
                                        <Select.Option key={key} value={key}>{value}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row> : null}
                <Row gutter={24}>
                    <Col flex={2.4}>
                        {/* Town Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="iban"/>}
                            name="iban"
                            initialValue={formOrganizationDetails.iban}
                            rules={[
                                {
                                    pattern: /^[0-9a-zA-Z]*$/,
                                    message: <FormattedMessage id="invalid_field"/>
                                },
                            ]}
                        >
                            <Input
                                className="dentobook-input"
                                value={formOrganizationDetails.iban ?? undefined}
                                onChange={e => formOrganizationDetails.iban = e.target.value}
                            />
                        </Form.Item>
                    </Col>
                    <Col flex={2.4}>
                        {/* Town Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input mb-64"}
                            label={<FormattedMessage id="accountable_person"/>}
                            name="accountable_person"
                            initialValue={formOrganizationDetails.accountablePerson}
                            rules={[
                                {
                                    pattern: /^([a-zA-Z\u0410-\u044F\s]+)$/
                                    ,
                                    message: <FormattedMessage id="letters_only_field"/>
                                },
                            ]}
                        >
                            <Input
                                className="dentobook-input"
                                value={formOrganizationDetails.accountablePerson ?? undefined}
                                onChange={e => formOrganizationDetails.accountablePerson = e.target.value}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col flex={1.7}>
                        {/* Invoice Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={
                                <span>
                <FormattedMessage id="invoiceNumber"/>
                <InfoCircleOutlined style={{marginLeft: 8, color: 'blue'}} onClick={() => showModal('invoice')}/>
              </span>
                            }
                            name="invoiceNumber"
                            initialValue={formOrganizationDetails.invoiceNumber}
                            rules={[
                                {
                                    pattern: /^[0-9a-zA-Z]*$/,
                                    message: <FormattedMessage id="invalid_field"/>
                                },
                                {
                                    len: 10,
                                    message: <FormattedMessage id="invoiceNumber_must_be_10_chars"
                                                               defaultMessage="Invoice number must be exactly 10 characters."/>
                                }
                            ]}
                        >
                            <Input
                                className="dentobook-input"
                                value={formOrganizationDetails.invoiceNumber ?? undefined}
                                onChange={e => formOrganizationDetails.invoiceNumber = e.target.value}
                                maxLength={10}
                                disabled={!isInvoiceNumberEditable}
                            />
                        </Form.Item>
                    </Col>
                    <Col flex={1.5}>
                        {/* Invoice Input */}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={
                                <span>
                <FormattedMessage id="proformaInvoiceNumber"/>
                <InfoCircleOutlined style={{marginLeft: 8, color: 'blue'}} onClick={() => showModal('proforma')}/>
              </span>
                            }
                            name="proformaInvoiceNumber"
                            initialValue={formOrganizationDetails.proformaInvoiceNumber}
                            rules={[
                                {
                                    pattern: /^[0-9a-zA-Z]*$/,
                                    message: <FormattedMessage id="invalid_field"/>
                                },
                                {
                                    len: 10,
                                    message: <FormattedMessage id="invoiceNumber_must_be_10_chars"
                                                               defaultMessage="Invoice number must be exactly 10 characters."/>
                                }
                            ]}
                        >
                            <Input
                                className="dentobook-input"
                                value={formOrganizationDetails.proformaInvoiceNumber ?? undefined}
                                onChange={e => formOrganizationDetails.proformaInvoiceNumber = e.target.value}
                                maxLength={10}
                                disabled={!isProformaInvoiceNumberEditable}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Modal
                    transitionName=""
                    className="dentobook-modal"
                    title={<FormattedMessage
                        id={modalType === 'invoice' ? "invoice_numbering_title" : "proforma_numbering_title"}/>}
                    open={isModalVisible}
                    onOk={handleModalOk}
                    onCancel={handleModalCancel}
                    footer={[
                        <div key="1" style={{display: 'flex', justifyContent: 'flex-end'}}>
                            <Button className={"dentobook-btn-secondary modal-btn"} key="close"
                                    onClick={handleModalCancel}>
                                <FormattedMessage id="close"/>
                            </Button>
                        </div>
                    ]}
                >
                    <p><FormattedMessage
                        id={modalType === 'invoice' ? "invoice_numbering_text" : "proforma_numbering_text"}/></p>
                </Modal>
                <br/>
                <br/>
                <FormButtons>
                    <Button
                        className={"dentobook-btn-secondary modal-btn"}
                        htmlType="button" onClick={() => setIsModalOpen(true)}>
                        {<FormattedMessage id="cancel"/>}
                    </Button>
                    <Button
                        className={"dentobook-btn-primary modal-btn"}
                        onClick={handleSubmit} htmlType="submit">
                        {<FormattedMessage id="save"/>}
                    </Button>
                </FormButtons>
            </Form>

            <InvoiceConfigSetupModal
                visible={showInvoiceConfigSetup}
                onSuccess={() => {
                    setShowInvoiceConfigSetup(false);
                    // Refetch invoice configurations after successful setup
                    const refetchConfigs = async () => {
                        if (organization.id != null) {
                            try {
                                const [invoiceResponse, proformaResponse] = await Promise.all([
                                    invoicesApi.fetchInvoiceConfiguration(organization.id, "INVOICE"),
                                    invoicesApi.fetchInvoiceConfiguration(organization.id, "PROFORMA_INVOICE")
                                ]);
                                setIsInvoiceNumberEditable(!invoiceResponse?.data);
                                setIsProformaInvoiceNumberEditable(!proformaResponse?.data);
                            } catch (error) {
                                logger.error('Error refetching invoice configurations:', error);
                            }
                        }
                    };
                    refetchConfigs();
                }}
                onCancel={() => setShowInvoiceConfigSetup(false)}
                organization={organization}
            />
        </>
    );

}

export default GeneralForm;